import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Breakpoint, Clickable, Stack, Text } from '@moda/om';
import ChevronDownIcon from '@moda/icons/chevron-down-12';
import ChevronUpIcon from '@moda/icons/chevron-up-12';
import ShippingAltIcon from '@moda/icons/shipping-alt-20';
import { hrefFor } from '../../../routers';
import { tc } from '../../../lib/trackingContext';
import { usePageType } from '../../../hooks/usePageType';
import { Link } from '../../Link';
import { SwiperCarousel } from '../../Swiper';
import { TrunkshowCountdownTimer } from '../../TrunkshowCountdownTimer';
import { PdpInfoContainerFragment } from '../../../generated/types';

import './PdpTrunkshowInfo.scss';

type PdpTrunkshow = NonNullable<
  NonNullable<
    NonNullable<NonNullable<PdpInfoContainerFragment['trunkshows']>['edges']>[number]
  >['node']
>;

interface Props {
  trunkshow?: PdpTrunkshow;
  shippingDateFormatted?: string;
  pdpVersion?: boolean;
  isDropshipItem?: boolean;
  header?: React.ReactNode;
}

export const PdpTrunkshowInfo: React.FC<Props> = ({
  trunkshow,
  shippingDateFormatted,
  pdpVersion,
  isDropshipItem,
  header
}) => {
  const { vertical = 'women' } = useParams<{ vertical?: undefined }>();
  const [expanded, setExpanded] = useState<boolean>();
  const pagetype = usePageType();

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      setExpanded(expanded => !expanded);
      tc.track('What is Trunkshow Clicked', {
        addToTrackingEvent: {
          pagetype,
          coordinateX: event?.pageX || 0,
          coordinateY: event?.pageY || 0
        }
      });
    },
    [pagetype]
  );

  useEffect(() => {
    if (
      (localStorage.getItem('TLPVisited') === null ||
        localStorage.getItem('TSPVisited') === null) &&
      (pagetype === 'TLP' || pagetype === 'TSP')
    ) {
      setExpanded(true);
    }
  }, [pagetype]);

  const contentArray = [
    <Stack key={1} space={3} direction="horizontal" className="PdpTrunkshowInfo__section-content">
      <Text className="PdpTrunkshowInfo__title-number" color="cement">
        1
      </Text>
      <div className="PdpTrunkshowInfo__text">
        <Text treatment="h6" className="PdpTrunkshowInfo__title-text">
          EXCLUSIVE
        </Text>
        <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description" color="cement">
          Designers don't produce every piece in their collection - but all styles, sizes, colors
          are available to you through trunkshows.
        </Text>
      </div>
    </Stack>,
    <Stack key={2} space={3} direction="horizontal" className="PdpTrunkshowInfo__section-content">
      <Text className="PdpTrunkshowInfo__title-number" color="cement">
        2
      </Text>
      <div className="PdpTrunkshowInfo__text">
        <Text treatment="h6" className="PdpTrunkshowInfo__title-text">
          LIMITED TIME
        </Text>
        <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description" color="cement">
          Trunkshows give you a small window to buy pieces before designers choose which pieces to
          produce.
        </Text>
      </div>
    </Stack>,
    <Stack key={3} space={3} direction="horizontal" className="PdpTrunkshowInfo__section-content">
      <Text className="PdpTrunkshowInfo__title-number" color="cement">
        3
      </Text>
      <div className="PdpTrunkshowInfo__text">
        <Text treatment="h6" className="PdpTrunkshowInfo__title-text">
          SET THE TREND
        </Text>
        <Text treatment="h6" className="PdpTrunkshowInfo__trunkshow-description" color="cement">
          Pre-order now and guarantee your access to next season's most fashionable items.
        </Text>
      </div>
    </Stack>
  ];

  const allowExtraTitle = header != undefined ? true : false;

  return (
    <Stack space={5} className="PdpTrunkshowInfo" data-testid="infoModuleSection">
      <div
        className={classNames('PdpTrunkshowInfo__main', {
          'PdpTrunkshowInfo__main--wide': !pdpVersion
        })}
      >
        {allowExtraTitle && (
          <div className="PdpTrunkshowInfo__title-grid">
            {header}
            <Clickable
              aria-label="Learn More"
              onClick={(event: React.MouseEvent) => {
                handleClick(event);
              }}
            >
              <Stack space={1} direction="horizontal" alignItems="center" justifyContent="flex-end">
                <Breakpoint gt="sm">
                  <Text>Learn More</Text>
                </Breakpoint>
                <div
                  className={classNames('PdpTrunkshowInfo__icons', {
                    'PdpTrunkshowInfo__icons--expanded': expanded
                  })}
                >
                  <ChevronUpIcon className="PdpTrunkshowInfo__icon-up" />
                  <ChevronDownIcon className="PdpTrunkshowInfo__icon-down" />
                </div>
              </Stack>
            </Clickable>
          </div>
        )}

        {!allowExtraTitle && (
          <div
            className={classNames('PdpTrunkshowInfo__title-new', {
              'PdpTrunkshowInfo__title-new--wide': !pdpVersion
            })}
          >
            {trunkshow && (
              <Link
                to={hrefFor.TrunkshowPage({
                  vertical,
                  trunkshowId: trunkshow.id,
                  trunkshowSlug: trunkshow?.slug as string
                })}
                className="PdpTrunkshowInfo__title-link"
              >
                <Text
                  treatment="bold1"
                  className={classNames('PdpTrunkshowInfo__trunkshow-name', {
                    'PdpTrunkshowInfo__trunkshow-name--wide': !pdpVersion
                  })}
                >
                  {trunkshow?.name} {trunkshow?.seasonName}
                </Text>
              </Link>
            )}

            <Clickable
              aria-label="Learn More"
              onClick={(event: React.MouseEvent) => {
                handleClick(event);
              }}
            >
              <Stack
                space={2}
                direction="horizontal"
                alignItems="center"
                className="PdpTrunkshowInfo__title-right"
              >
                <Text>Learn More</Text>

                <div
                  className={classNames('PdpTrunkshowInfo__icons', {
                    'PdpTrunkshowInfo__icons--expanded': expanded
                  })}
                >
                  <ChevronUpIcon className="PdpTrunkshowInfo__icon-up" />
                  <ChevronDownIcon className="PdpTrunkshowInfo__icon-down" />
                </div>
              </Stack>
            </Clickable>
          </div>
        )}

        {expanded && (
          <>
            <Breakpoint gt="sm">
              <div
                className={classNames('PdpTrunkshowInfo__expanded', {
                  'PdpTrunkshowInfo__expanded--wide': !pdpVersion
                })}
              >
                {contentArray.map((section, index) => (
                  <div
                    key={index}
                    className={classNames('PdpTrunkshowInfo__section', {
                      'PdpTrunkshowInfo__section--wide': !pdpVersion
                    })}
                  >
                    {section}
                  </div>
                ))}
              </div>
            </Breakpoint>

            <Breakpoint lt="sm">
              <SwiperCarousel
                identifier="PdpTrunkshowInfo"
                className={classNames('PdpTrunkshowInfo__swiper', {
                  'PdpTrunkshowInfo__swiper--wide': !pdpVersion
                })}
              >
                {contentArray.map((section, index) => (
                  <div key={index} className="PdpTrunkshowInfo__section-swiper">
                    {section}
                  </div>
                ))}
              </SwiperCarousel>
            </Breakpoint>
          </>
        )}
      </div>

      {pdpVersion && (
        <Stack space={2} className="PdpTrunkshowInfo__extra-info">
          {!isDropshipItem && (
            <div className="PdpTrunkshowInfo__shipping">
              <ShippingAltIcon className="PdpTrunkshowInfo__shipping_icon" />
              Ships by {shippingDateFormatted}
            </div>
          )}

          {trunkshow?.activeEndI && (
            <TrunkshowCountdownTimer
              className="PdpTrunkshowInfo__timer"
              endingText="Trunkshow ends in"
              countdownTime={trunkshow?.activeEndI}
              countdownEndMessage="This Trunkshow Has Expired"
              hasZeroes={false}
              hasIcon
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
